import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Plus, Clock, CheckCircle2, TrendingDown, CalendarDays, ListFilter, Layers, ArrowLeftRight,
} from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AppLayout } from "@/components/AppLayout";
import { LeaveBalanceCard } from "@/components/leave/LeaveBalanceCard";
import { LeaveListView } from "@/components/leave/LeaveListView";
import { LeaveDetailModal } from "@/components/leave/LeaveDetailModal";
import { LeaveRequestDialog } from "@/components/leave/LeaveRequestDialog";
import { useAuth } from "@/contexts/AuthContext";
import {
  apiGetMyLeaveBalance,
  apiGetMyLeaveRequests,
  apiGetLeaveTypes,
  apiGetMyLeaveRequest,
  apiGetMyLeaveReplacementAssignments,
  type LeaveReplacementAssignment,
} from "@/lib/api";
import { useDateLocale } from "@/hooks/useDateLocale";
import { getLeaveStatusLabel } from "@/lib/leave-i18n";

const CURRENT_YEAR = new Date().getFullYear();

// ── Stat card (matches Organization / Certifications pattern) ─────────────────

function StatCard({
  label, value, icon: Icon, bg, color, loading,
}: {
  label: string; value: number | string; icon: any;
  bg: string; color: string; loading?: boolean;
}) {
  return (
    <div className="flex items-center gap-3.5 px-5 py-4">
      <div className={`h-9 w-9 rounded-xl ${bg} flex items-center justify-center shrink-0`}>
        <Icon className={`h-4 w-4 ${color}`} />
      </div>
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        {loading ? (
          <Skeleton className="h-7 w-10" />
        ) : (
          <p className="text-2xl font-bold tabular-nums text-foreground leading-none">{value}</p>
        )}
      </div>
    </div>
  );
}

function ReplacementAssignmentsCard({
  assignments,
  loading,
}: {
  assignments: LeaveReplacementAssignment[];
  loading: boolean;
}) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();

  if (!loading && assignments.length === 0) return null;

  function fmt(dateStr: string) {
    try {
      return format(new Date(`${dateStr}T00:00:00`), "d MMM yyyy", { locale: dateLocale });
    } catch {
      return dateStr;
    }
  }

  return (
    <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
      <div className="px-5 py-3.5 border-b border-border/60 flex items-center gap-2.5">
        <div className="h-8 w-8 rounded-xl bg-sky-500/10 flex items-center justify-center shrink-0">
          <ArrowLeftRight className="h-4 w-4 text-sky-600" />
        </div>
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-foreground">
            {t("leave.replacementAssignmentsTitle")}
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t("leave.replacementAssignmentsSubtitle")}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="p-4 space-y-3">
          <Skeleton className="h-16 rounded-xl" />
          <Skeleton className="h-16 rounded-xl" />
        </div>
      ) : (
        <div className="divide-y divide-border/60">
          {assignments.map((assignment) => {
            const leaveTypeLabel = t(`leave.types.${assignment.leaveType}`, {
              defaultValue: assignment.leaveTypeLabel ?? assignment.leaveType,
            });

            return (
              <div key={assignment.id} className="p-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div className="space-y-2 min-w-0">
                  <p className="text-sm font-semibold text-foreground">
                    {t("leave.youAreReplacing", { employeeName: assignment.employeeName })}
                  </p>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span>
                      {t("leave.replacementPeriod", {
                        startDate: fmt(assignment.startDate),
                        endDate: fmt(assignment.endDate),
                      })}
                    </span>
                    <span>
                      {t("leave.replacementLeaveType", { leaveType: leaveTypeLabel })}
                    </span>
                  </div>
                  {assignment.notes && (
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {t("leave.replacementNotes", { notes: assignment.notes })}
                    </p>
                  )}
                </div>
                <Badge variant="secondary" className="w-fit shrink-0">
                  {t("leave.replacementStatus", {
                    status: getLeaveStatusLabel(t, assignment.status),
                  })}
                </Badge>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

const LeaveManagement = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("dashboard");

  const newRequestParam = searchParams.get("newRequest");
  const [requestDialogOpen, setRequestDialogOpen] = useState(newRequestParam === "1");
  const openRequestId = searchParams.get("openRequest");

  const { data: urlOpenRequest, isError: urlRequestError } = useQuery({
    queryKey: ["myLeaveRequest", openRequestId],
    queryFn: () => apiGetMyLeaveRequest(Number(openRequestId)),
    enabled: !!openRequestId,
    staleTime: 0,
    retry: false,
  });

  useEffect(() => {
    if (urlRequestError && openRequestId) {
      toast.error(t("leave.couldNotLoadRequest"));
      const params = new URLSearchParams(searchParams);
      params.delete("openRequest");
      setSearchParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlRequestError]);

  function handleUrlRequestClose() {
    const params = new URLSearchParams(searchParams);
    params.delete("openRequest");
    setSearchParams(params, { replace: true });
  }

  // ── Queries ────────────────────────────────────────────────────────────────

  const { data: balances = [], isLoading: balancesLoading } = useQuery({
    queryKey: ["myLeaveBalance", CURRENT_YEAR],
    queryFn: () => apiGetMyLeaveBalance(CURRENT_YEAR),
  });

  const { data: myRequests = [], isLoading: myRequestsLoading } = useQuery({
    queryKey: ["myLeaveRequests"],
    queryFn: () => apiGetMyLeaveRequests(),
  });

  const {
    data: replacementAssignments = [],
    isLoading: replacementAssignmentsLoading,
  } = useQuery({
    queryKey: ["myLeaveReplacementAssignments"],
    queryFn: () => apiGetMyLeaveReplacementAssignments(),
  });

  const { data: leaveTypes = [] } = useQuery({
    queryKey: ["leaveTypes"],
    queryFn: apiGetLeaveTypes,
  });

  // ── Stats ──────────────────────────────────────────────────────────────────

  const annualBalance = balances.find((b) => b.leaveType === "annual");
  const annualTaken   = annualBalance?.usedDays ?? 0;
  const otherBalances = balances.filter((b) => b.leaveType !== "annual");
  const otherTaken    = otherBalances.reduce((sum, b) => sum + b.usedDays, 0);
  const otherBreakdown = otherBalances.filter((b) => b.usedDays > 0);
  const pendingCount  = myRequests.filter((r) => r.status === "Pending").length;
  const approvedCount = myRequests.filter(
    (r) => r.status === "Approved" && new Date(r.startDate).getFullYear() === CURRENT_YEAR,
  ).length;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <AppLayout>
      <div className="space-y-6 w-full">
        {/* Header */}
        <div className="flex items-center justify-between animate-in fade-in slide-in-from-left-5 duration-500 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
              <CalendarDays className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{t("leave.title")}</h1>
              <p className="text-sm text-muted-foreground mt-1">{t("leave.subtitle")}</p>
            </div>
          </div>
          <Button onClick={() => setRequestDialogOpen(true)} className="gradient-brand text-primary-foreground">
            <Plus className="h-4 w-4 mr-1.5" />
            {t("leave.requestLeave")}
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="animate-in fade-in slide-in-from-bottom-4 duration-500 delay-100">
          <TabsList className="w-full justify-start bg-transparent h-auto p-0 border-b border-border/60 rounded-none gap-0 overflow-x-auto overflow-y-hidden">
            <TabsTrigger
              value="dashboard"
              className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent"
            >
              <CalendarDays className="h-3.5 w-3.5" />
              {t("leave.dashboardTab")}
            </TabsTrigger>
            <TabsTrigger
              value="requests"
              className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent"
            >
              <ListFilter className="h-3.5 w-3.5" />
              {t("leave.myRequestsTab")}
              {pendingCount > 0 && (
                <span className="ml-1 rounded-full bg-amber-500/15 text-amber-700 text-[10px] font-semibold px-1.5 py-0.5 leading-none">
                  {pendingCount}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* ── Dashboard tab ─────────────────────────────────────────────── */}
          <TabsContent value="dashboard" className="mt-6 space-y-6">
            {/* Stat cards */}
            <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 lg:divide-x sm:divide-x divide-border/60 divide-y sm:divide-y-0 sm:[&>*:nth-child(3)]:border-t lg:[&>*:nth-child(3)]:border-t-0 sm:[&>*:nth-child(4)]:border-t lg:[&>*:nth-child(4)]:border-t-0">
                <StatCard
                  label={`${t("leave.annualDaysApproved")} · ${CURRENT_YEAR}`}
                  value={annualTaken}
                  icon={TrendingDown}
                  bg="bg-blue-500/10"
                  color="text-blue-600"
                  loading={balancesLoading}
                />
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:bg-muted/40"
                      aria-label={t("leave.otherDaysApproved")}
                    >
                      <StatCard
                        label={`${t("leave.otherDaysApproved")} · ${CURRENT_YEAR}`}
                        value={otherTaken}
                        icon={Layers}
                        bg="bg-violet-500/10"
                        color="text-violet-600"
                        loading={balancesLoading}
                      />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-72 p-3">
                    <div className="text-xs font-semibold text-foreground mb-2">
                      {t("leave.otherDaysApproved")} · {CURRENT_YEAR}
                    </div>
                    {otherBreakdown.length === 0 ? (
                      <p className="text-xs text-muted-foreground py-1">
                        {t("leave.noOtherLeaves")}
                      </p>
                    ) : (
                      <ul className="space-y-1.5">
                        {otherBreakdown.map((b) => (
                          <li
                            key={b.leaveType}
                            className="flex items-center justify-between gap-3 text-xs"
                          >
                            <span className="flex items-center gap-2 min-w-0">
                              <span
                                className="h-2 w-2 rounded-full shrink-0"
                                style={{ backgroundColor: b.leaveTypeColor || "#94a3b8" }}
                              />
                              <span className="truncate text-foreground">
                                {b.leaveTypeLabel}
                              </span>
                            </span>
                            <span className="tabular-nums font-medium text-foreground">
                              {b.usedDays}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </PopoverContent>
                </Popover>
                <StatCard
                  label={`${t("leave.pending")} · ${t("leave.awaitingApproval")}`}
                  value={pendingCount}
                  icon={Clock}
                  bg="bg-amber-500/10"
                  color="text-amber-600"
                  loading={myRequestsLoading}
                />
                <StatCard
                  label={`${t("leave.approved")} · ${CURRENT_YEAR}`}
                  value={approvedCount}
                  icon={CheckCircle2}
                  bg="bg-emerald-500/10"
                  color="text-emerald-600"
                  loading={myRequestsLoading}
                />
              </div>
            </div>

            <ReplacementAssignmentsCard
              assignments={replacementAssignments}
              loading={replacementAssignmentsLoading}
            />

            {/* Leave balances */}
            <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
              <div className="px-5 py-3.5 border-b border-border/60">
                <h2 className="text-sm font-semibold text-foreground">
                  {t("leave.myBalance")}
                  <span className="ml-1.5 text-muted-foreground font-normal">({CURRENT_YEAR})</span>
                </h2>
              </div>
              <div className="p-4">
                {balancesLoading ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <Skeleton className="h-36 rounded-xl" />
                  </div>
                ) : !annualBalance ? (
                  <div className="py-10 flex flex-col items-center gap-2 text-center">
                    <CalendarDays className="h-8 w-8 text-muted-foreground/30" />
                    <p className="text-sm text-muted-foreground">{t("leave.noBalances")}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <LeaveBalanceCard balance={annualBalance} />
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ── My Requests tab ──────────────────────────────────────────── */}
          <TabsContent value="requests" className="mt-6">
            <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
              <div className="px-5 py-3.5 border-b border-border/60 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-foreground">
                  {t("leave.myRequests")}
                  {!myRequestsLoading && myRequests.length > 0 && (
                    <span className="ml-1.5 text-muted-foreground font-normal">
                      {" - "}
                      {t("leave.requestCount", { count: myRequests.length })}
                    </span>
                  )}
                </h2>
                {pendingCount > 0 && (
                  <span className="text-xs text-amber-600 font-medium">
                    {t("leave.pendingCountLabel", { count: pendingCount })}
                  </span>
                )}
              </div>
              {myRequestsLoading ? (
                <div className="p-4">
                  <Skeleton className="h-48 rounded-xl" />
                </div>
              ) : (
                <LeaveListView
                  requests={myRequests}
                  leaveTypes={leaveTypes}
                  showEmployee={false}
                />
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Dialogs */}
        <LeaveRequestDialog
          open={requestDialogOpen}
          onOpenChange={setRequestDialogOpen}
          balances={balances}
          currentEmployeeId={user?.employeeId ?? undefined}
        />
        <LeaveDetailModal
          request={urlOpenRequest ?? null}
          open={!!urlOpenRequest}
          onOpenChange={(v) => { if (!v) handleUrlRequestClose(); }}
        />
      </div>
    </AppLayout>
  );
};

export default LeaveManagement;
